/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import { ICoreContext, IGeneric, IAny, IImageSettings, Image, IImageData } from '@msdyn365-commerce/core';
import { CommerceList, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ProductComponent } from '../../../themes/only/views/components/product.component';
import classnames from 'classnames';

import * as React from 'react';

import {
    ICampaignLinkData,
    ICampaignItemsData,
    IOnlyCSearchResultContainerConfig,
    IOnlyCSearchResultContainerResources,
    ICampaignTitleData
} from '../only-c-search-result-container.props.autogenerated';
import { ProductSearchResultExtended } from '../actions/get-full-products-by-collection';
import { _readFromSessionStorage, _writeToSessionStorage } from '../../../utils/helper';

interface IProductSearchResultItems {
    products: ProductSearchResultExtended[];
    context: ICoreContext;
    resources: IOnlyCSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    config: IOnlyCSearchResultContainerConfig;
    wishlist?: CommerceList[];
    pageNumber: number;
    categoryId: number;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
export function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

// VSI Customization to render multiple campaigns
function renderCampaign(
    index: number,
    campaignItems: ICampaignItemsData[] | undefined,
    context?: ICoreContext<IGeneric<IAny>>,
    imageSettings?: IImageSettings
) {
    const campaign = campaignItems?.find(c => c?.campaignIndex && c?.campaignIndex > 0 && c?.campaignIndex === index + 1);
    return campaign
        ? advertisement(
              campaign?.campaignImage,
              campaign?.campaignLink,
              campaign?.campaignTitle,
              campaign?.campaignText,
              campaign?.campaignTextWidthAlignment,
              campaign?.campaignTextHeightAlignment,
              context,
              imageSettings
          )
        : null;
}

// VSI Customization to render advertisements
function advertisement(
    campaignImage?: IImageData,
    campaignLink?: ICampaignLinkData,
    campaignTitle?: ICampaignTitleData,
    campaignText?: string,
    campaignTextWidthAlignment?: string,
    campaignTextHeightAlignment?: string,
    context?: ICoreContext<IGeneric<IAny>>,
    imageSettings?: IImageSettings
) {
    const PromotionalImageProps = {
        gridSettings: context?.request.gridSettings || {},
        imageSettings: imageSettings
    };
    return (
        <li className='ms-product-search-result__item'>
            {
                <div className='msc-product__image container'>
                    {campaignLink ? (
                        <a
                            href={campaignLink.linkUrl && campaignLink.linkUrl.destinationUrl}
                            className='msc-product'
                            title={campaignLink.linkText}
                            aria-label={campaignLink.ariaLabel}
                            target={campaignLink.openInNewTab ? '_blank' : undefined}
                            rel='noreferrer'
                        >
                            {campaignLink.linkText ? (
                                <span className={classnames(campaignTextWidthAlignment, campaignTextHeightAlignment)}>
                                    {campaignLink.linkText}
                                </span>
                            ) : (
                                ''
                            )}
                            <span className='overlay' />
                            {campaignImage && (
                                <div className='msc-product__image'>
                                    <Image {...campaignImage} {...PromotionalImageProps} />
                                </div>
                            )}
                        </a>
                    ) : (
                        <div className='msc-product__image container'>
                            <div className='msc-product'>
                                <span className='overlay' />
                                {campaignImage && (
                                    <div className='msc-product__image'>
                                        <Image {...campaignImage} {...PromotionalImageProps} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            }
            {campaignLink ? (
                <div className='msc-product__details'>
                    <a
                        href={campaignLink.linkUrl && campaignLink.linkUrl.destinationUrl}
                        className='msc-product'
                        title={campaignTitle?.text}
                        aria-label={campaignLink.ariaLabel}
                        target={campaignLink.openInNewTab ? '_blank' : undefined}
                        rel='noreferrer'
                    >
                        <Msdyn365.Text
                            {...campaignTitle}
                            className='msc-campaign__title'
                            tag={campaignTitle?.tag || 'h2'}
                            text={campaignTitle?.text}
                        />
                        <h4 className='msc-product__title'>{campaignText}</h4>
                    </a>
                </div>
            ) : null}
        </li>
    );
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    wishlist,
    pageNumber,
    categoryId,
    config: { campaignItems, sizesHeading, maxSizeDisplay, favouritesContent, favouritesHeading, wishListLink, variantsToShow }
}) => {
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    // VSI Customization for scroll restoration
    const _persistProduct = (event: React.MouseEvent<HTMLLIElement>) => {
        const pid = event.currentTarget.getAttribute('data-pid') || '0';
        _writeToSessionStorage('pid', context, pid);
        _writeToSessionStorage('pageNo', context, pageNumber?.toString());
        _writeToSessionStorage('products', context, JSON.stringify(products));
        _writeToSessionStorage('pCategoryId', context, categoryId?.toString());
    };
    const pid = parseInt(_readFromSessionStorage('pid', context) || '0', 10);
    const _restorationRef = React.createRef<HTMLLIElement>();

    React.useEffect(() => {
        if (!_restorationRef || !pid) {
            return;
        }
        window.scrollTo({ behavior: 'smooth', top: _restorationRef?.current?.offsetTop || 0 });
    }, [_restorationRef]);
    const itemIds = Array.from(new Set(products.map(item => item.ItemId)));
    return (
        <>
            {/* Idea was to tag li elements with product item id class, then can hide any variants over 3rd with nth-child,
            but nth-child doesn't take class into accout so using sibling combinator instead
            Thanks to https://betterprogramming.pub/css-select-nth-element-with-class-a313d080e2bf */}
            {variantsToShow! !== undefined && variantsToShow > 0 && (
                <style type='text/css'>
                    {itemIds.map(itemId => {
                        let cssSelector = `li.item-${itemId}`;
                        for (let i = 0; i < variantsToShow; i++) {
                            cssSelector += ` ~ li.item-${itemId}`;
                        }
                        return <>{`${cssSelector} {display:none;}`}</>;
                    })}
                </style>
            )}

            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResultExtended, index: number) => {
                    return (
                        <>
                            {renderCampaign(index, campaignItems, context, imageSettings)}
                            <li
                                data-pid={index}
                                className={`ms-product-search-result__item item-${product.ItemId}`}
                                key={index}
                                onClick={_persistProduct}
                                ref={index === pid ? _restorationRef : null}
                                data-itemid={product.ItemId}
                            >
                                <ProductComponent
                                    context={context}
                                    telemetryContent={telemetryContent}
                                    imageSettings={imageSettings}
                                    freePriceText={resources.priceFree}
                                    originalPriceText={resources.originalPriceText}
                                    currentPriceText={resources.currentPriceText}
                                    ratingAriaLabel={resources.ratingAriaLabel}
                                    allowBack={allowBackNavigation}
                                    id={moduleId}
                                    key={product.RecordId}
                                    typeName={moduleType}
                                    data={{ product }}
                                    quickViewButton={quickviewSlot}
                                    inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                                    isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                                    priceResources={priceResources}
                                    swatchItemAriaLabel={resources.swatchItemAriaLabel}
                                    dimensionAvailabilities={productsDimensionAvailabilities?.find(
                                        dimensionAvailability =>
                                            ArrayExtensions.hasElements(dimensionAvailability) &&
                                            dimensionAvailability[0].masterProductId === product.MasterProductId
                                    )}
                                    wishlist={wishlist}
                                    sizesHeading={sizesHeading}
                                    newArrivalMsg={resources.newArrivalMsg}
                                    productLifeTag={resources.productLifeTag}
                                    maxSizeDisplay={maxSizeDisplay || 9}
                                    splitSizeText={resources.splitSizeText}
                                    colorText={resources.colorText}
                                    favouritesContent={favouritesContent}
                                    favouritesHeading={favouritesHeading}
                                    wishListLink={wishListLink}
                                />
                            </li>
                        </>
                    );
                })}
            </ul>
        </>
    );
};
